import React, { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";

export type ConfirmationType = "danger" | "warning" | "info" | "success";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  message?: string;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  type?: ConfirmationType;
  isLoading?: boolean;
  showCloseButton?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  message = "Are you sure you want to proceed?",
  title = "Confirm Action",
  confirmText = "Yes, Confirm",
  cancelText = "Cancel",
  type = "danger",
  isLoading = false,
  showCloseButton = true,
}) => {
  // Get icon and colors based on type
  const getTypeConfig = useCallback(() => {
    switch (type) {
      case "danger":
        return {
          icon: "ti-trash-x",
          avatarClass: "bg-transparent-danger text-danger",
          confirmBtnClass: "btn-danger",
        };
      case "warning":
        return {
          icon: "ti-alert-triangle",
          avatarClass: "bg-transparent-warning text-warning",
          confirmBtnClass: "btn-warning",
        };
      case "info":
        return {
          icon: "ti-info-circle",
          avatarClass: "bg-transparent-info text-info",
          confirmBtnClass: "btn-info",
        };
      case "success":
        return {
          icon: "ti-check",
          avatarClass: "bg-transparent-success text-success",
          confirmBtnClass: "btn-success",
        };
      default:
        return {
          icon: "ti-alert-triangle",
          avatarClass: "bg-transparent-warning text-warning",
          confirmBtnClass: "btn-warning",
        };
    }
  }, [type]);

  const typeConfig = getTypeConfig();

  // Handle confirm action
  const handleConfirm = useCallback(() => {
    onConfirm();
    if (!isLoading) {
      onClose();
    }
  }, [onConfirm, onClose, isLoading]);

  // Handle escape key
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [onClose, isOpen, isLoading]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) {
          onClose();
        }
      }}>
      <div
        className="modal-content"
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "0",
          width: "90%",
          maxWidth: "500px",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}>
        {/* Close button in header if enabled */}
        {showCloseButton && (
          <div
            className="modal-header"
            style={{
              padding: "1rem",
              borderBottom: "1px solid #e9ecef",
              position: "relative",
            }}>
            <button
              type="button"
              className="btn-close"
              style={{
                position: "absolute",
                right: "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                fontSize: "1.5rem",
                cursor: "pointer",
              }}
              onClick={onClose}
              disabled={isLoading}
              aria-label="Close">
              ×
            </button>
          </div>
        )}

        <div
          className="modal-body text-center"
          style={{
            padding: showCloseButton ? "1rem 2rem 2rem" : "2rem",
          }}>
          {/* Icon */}
          <span className={`avatar avatar-xl ${typeConfig.avatarClass} mb-3`}>
            <i className={`ti ${typeConfig.icon} fs-36`} />
          </span>

          {/* Title */}
          <h4 className="mb-2 fw-semibold">{title}</h4>

          {/* Message */}
          <p className="mb-4 text-muted fs-14">{message}</p>

          {/* Action Buttons */}
          <div className="d-flex justify-content-center gap-3">
            <button
              type="button"
              className="btn btn-light px-4"
              onClick={onClose}
              disabled={isLoading}>
              {cancelText}
            </button>

            <button
              type="button"
              className={`btn ${typeConfig.confirmBtnClass} px-4`}
              onClick={handleConfirm}
              disabled={isLoading}>
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Processing...
                </>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationModal;
